import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { ErrorAPIResult } from '../../../../../redux/api/utils/isSuccessfulResult';
import { LoadingView } from '../../../../common/views/LoadingView';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { PromoteContainer } from '../../PromoteContainer';
import { SerializedReel } from '../../types';
import { ReelProgress } from './ReelProgress';
import { useReelPoll } from './useReelPoll';
import { isReelWorking } from './reelStages';
import { reelMessages, reelVoiceMessages } from './reelMessages';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';

import { smlNavigationMessages } from '../../../navigation/smlNavigationMessages';
import { reelProgressMessages } from './reelProgressMessages';
import { ReelAiChip } from './ReelAiChip';
import { ReelReviewPage } from './ReelReviewPage';
import { ReelHistoryStrip } from './ReelHistoryStrip';

/** The statuses that show the preview (design c) instead of the stage list. */
const REVIEW_STATUSES: ReadonlyArray<SerializedReel['status']> = ['ready', 'approved', 'rejected'];

interface ReelProgressPageProps {
  accountId: number;
  reelUuid: string;
  items?: CatalogItem[];
  isRetrying: boolean;
  /** The error of a failed "Try again" on this reel. */
  tryAgainError?: ErrorAPIResult['error'];
  onTryAgain: (reel: SerializedReel) => void;
  onChangeSong: (reel: SerializedReel) => void;
  onChangeSettings: (reel: SerializedReel) => void;
}

/**
 * The page of one reel, opened by `?reel=<uuid>`. It polls the reel while it works (design b1 to
 * b3) and shows the preview of a ready, approved or rejected reel (design c).
 */
const ReelProgressPageComponent: React.FC<ReelProgressPageProps> = (props) => {
  const { accountId, reelUuid, items, isRetrying, tryAgainError, onTryAgain, onChangeSong, onChangeSettings } = props;
  const intl = useIntl();
  const { reel, error, refetch, lastSeenStage } = useReelPoll({ accountId, reelUuid });

  const release = useMemo(
    () => (reel === undefined ? undefined : items?.find((item) => item.uuid === reel.catalogItemUuid)),
    [items, reel]
  );
  const studioTitle = intl.formatMessage(smlNavigationMessages.promoteStudio);
  const crumbs = useMemo(
    () => (release === undefined ? [studioTitle] : [studioTitle, release.title]),
    [release, studioTitle]
  );

  const releaseTitle = release?.title;
  const themeId = reel?.themeId;
  const voiceId = reel?.voiceId;
  const durationSec = reel?.durationSec;
  const subtitle = useMemo(() => {
    if (themeId === undefined || voiceId === undefined || durationSec === undefined) {
      return '';
    }
    return [
      releaseTitle,
      intl.formatMessage(releaseThemeMessages[themeId].name),
      intl.formatMessage(voiceId === null ? reelMessages.noVoiceover : reelVoiceMessages[voiceId].label),
      intl.formatMessage(reelMessages.seconds, { count: durationSec }),
    ]
      .filter((part): part is string => part !== undefined)
      .join(' · ');
  }, [durationSec, intl, releaseTitle, themeId, voiceId]);

  if (reel === undefined) {
    if (error !== undefined) {
      return (
        <PromoteContainer title={studioTitle}>
          <ErrorView error={error} retry={() => refetch().catch(console.error)} />
        </PromoteContainer>
      );
    }
    return <LoadingView />;
  }

  if (REVIEW_STATUSES.includes(reel.status)) {
    return (
      <ReelReviewPage
        accountId={accountId}
        reel={reel}
        releaseTitle={release?.title}
        crumbs={crumbs}
        refetch={() => refetch().catch(console.error)}
        onNewReel={() => onChangeSettings(reel)}
      />
    );
  }

  const title = isReelWorking(reel.status) ? reelProgressMessages.titleWorking : reelProgressMessages.titleFailed;

  return (
    <PromoteContainer
      title={intl.formatMessage(title)}
      subtitle={subtitle}
      crumbs={crumbs}
      actions={reel.status === 'failed' ? undefined : <ReelAiChip />}
    >
      <ReelProgress
        reel={reel}
        lastSeenStage={lastSeenStage}
        releaseTitle={release?.title}
        artistName={release?.artistName}
        isRetrying={isRetrying}
        tryAgainError={tryAgainError}
        onTryAgain={() => onTryAgain(reel)}
        onChangeSong={() => onChangeSong(reel)}
        onChangeSettings={() => onChangeSettings(reel)}
      />
      {reel.status === 'failed' && reel.catalogItemUuid !== null && (
        <div className="mt-3">
          <ReelHistoryStrip
            accountId={accountId}
            catalogItemUuid={reel.catalogItemUuid}
            currentUuid={reel.uuid}
            releaseTitle={release?.title}
          />
        </div>
      )}
    </PromoteContainer>
  );
};

export const ReelProgressPage = TypedMemo(ReelProgressPageComponent);
